import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useForm } from '../../../../lib/components';
import { useStore } from '../../../../hooks/useStore';
import { PersonalDataView } from './view';
import { PersonalDataForm } from './form';
import TranslationHelper from '../shared/translationHelper';
import * as Yup from 'yup';
import Util from '../../../../misc/util';
import { ProfileViewEditBox } from '../shared/profileViewEditBox';
import { useUpdateEmployeeExtendedData } from '../../../../queries/employee/mutations';
import { useGetEmployee } from '../../../../queries/employee';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@pxtra-client-lib-legacy/gen';

export const EmployeeProfilPersonalData = observer(() => {
  const { employeeStore } = useStore();
  const { employeeId } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const helper = new TranslationHelper(t);
  const form = useForm();

  const { mutate: updateEmployeeData } = useUpdateEmployeeExtendedData({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      enqueueSnackbar(t('common:savingSuccess'), {
        variant: 'success'
      });
    },
    onError: (error: ApiError) => {
      if (error.body?.code === 'email_already_taken') {
        enqueueSnackbar(t('form:EMAIL_ALREADY_TAKEN'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('template:SAVING_ERROR'), { variant: 'error' });
      }
    }
  });

  const { data: extendedEmployeeData } = useGetEmployee(employeeStore.currentEmployee?.id ?? '');
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  const selectedGenderObject = helper.gender.options.find(
    (opt) => opt.value === extendedEmployeeData?.general_data?.gender
  );
  const selectedGender = selectedGenderObject ? selectedGenderObject.value : '';

  const selectedMarriageStatusObject = helper.marriageStatus.options.find(
    (opt) => opt.value === extendedEmployeeData?.general_data?.marriage_status
  );
  const selectedMarriageStatus = selectedMarriageStatusObject
    ? selectedMarriageStatusObject.value
    : '';

  const userEmailOrigin = extendedEmployeeData?.user?.email_origin;

  const renderView = observer(() => {
    return (
      <PersonalDataView
        data={extendedEmployeeData?.general_data}
        user={extendedEmployeeData?.user}
        userEmailOrigin={userEmailOrigin}
      />
    );
  });

  const initialValues = useMemo(
    () => ({
      first_name: extendedEmployeeData?.general_data?.first_name ?? '',
      last_name: extendedEmployeeData?.general_data?.last_name ?? '',
      birth_last_name: extendedEmployeeData?.general_data?.birth_last_name ?? '',
      birth_date: extendedEmployeeData?.general_data?.birth_date ?? '',
      gender: selectedGender,
      nationality: extendedEmployeeData?.general_data?.nationality ?? '',
      marriage_status: selectedMarriageStatus,
      login: extendedEmployeeData?.user?.email,
      missing_fields: extendedEmployeeData?.general_data?.missing_fields
    }),
    [
      extendedEmployeeData?.general_data?.first_name,
      extendedEmployeeData?.general_data?.last_name,
      extendedEmployeeData?.general_data?.birth_last_name,
      extendedEmployeeData?.general_data?.birth_date,
      selectedGender,
      extendedEmployeeData?.general_data?.nationality,
      selectedMarriageStatus,
      extendedEmployeeData?.user?.email,
      extendedEmployeeData?.general_data?.missing_fields?.join('|')
    ]
  );

  const renderEdit = () => {
    return (
      <PersonalDataForm
        userEmailOrigin={userEmailOrigin}
        initialValues={initialValues}
        validationSchema={{
          first_name: Yup.string()
            .max(200, t('form:MAX_LENGTH_EXCEEDED', { length: 200 }))
            .required(t('form:FIELD_IS_REQUIRED')),
          last_name: Yup.string()
            .max(200, t('form:MAX_LENGTH_EXCEEDED', { length: 200 }))
            .required(t('form:FIELD_IS_REQUIRED')),
          login: Yup.string().email(t('form:INVALID_EMAIL')),
          birth_last_name: Yup.string().max(200, t('form:MAX_LENGTH_EXCEEDED', { length: 200 })),
          birth_date: Yup.date()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError(t('form:INVALID_DATE'))
            .min(Util.getMinBirthDate(), t('form:INVALID_DATE'))
        }}
        formRef={form.formRef}
        onSubmit={async (values) => {
          try {
            if (employeeId) {
              const { login, ...generalDataValues } = values;

              const normalizedGeneralData = {
                ...generalDataValues,
                birth_date:
                  generalDataValues.birth_date === '' ? null : generalDataValues.birth_date
              };

              updateEmployeeData({
                employeeId,
                requestBody: {
                  general_data: normalizedGeneralData,
                  ...(login ? { user: { email: login } } : {})
                }
              });
            }
          } catch (error) {
            console.error(error);
          }
        }}
        onStateChanged={setCanSubmit}
      />
    );
  };

  return (
    <ProfileViewEditBox
      title={t('common:PERSONAL_DATA')}
      view={renderView}
      edit={renderEdit}
      canEdit
      canSubmit={canSubmit}
      onSubmit={form.handleSubmit}
      onCancel={form.handleReset}
    />
  );
});
